// ---------------------------------------------------------------------------
// The browser side of authentication.
//
// NO COLOUR, NO FONT, NO SPACING, AND NO REACT. V4 has now ported the Warm
// Professional tokens from site/css/styles.css into web/src/design/tokens.css,
// and that file is the ONLY place in web/src where a colour may be written out.
// scripts/verify-design-parity.sh refuses one anywhere else — in a value or in
// a comment, because a check that tried to tell prose from code would be a
// check with an exemption shaped like a commented-out palette.
//
// This file is pure request construction, so every property below is assertable
// under `node --test` with no DOM, no jsdom and no component-test stack.
//
// THE SESSION TOKEN NEVER APPEARS IN THIS FILE, and that is the design rather
// than an omission. The cookie is `__Host-` and HttpOnly, so JavaScript cannot
// read it — which is the entire point: an XSS that can run script in this origin
// still cannot exfiltrate a session. Anything here that "stored the token" would
// be undoing the strongest control the auth system has.
// ---------------------------------------------------------------------------

/** §4.3 — an HTML form cannot set a custom header, which is what defeats form-based CSRF. */
export const CSRF_HEADER = "X-Verbunk-Request";
export const CSRF_HEADER_VALUE = "1";

/** Chooses the session idle window (§4.1) and nothing else. Never an authorisation input. */
export type Surface = "console" | "worker";

export type RequestSpec = {
  // WIDENED AT V5. The console edits and withdraws things, so PATCH and DELETE
  // exist now. They are listed here rather than cast at each call site because
  // `access-control-allow-methods` has to name them too, and a method the
  // client can express but the preflight does not admit is the V4 CORS defect
  // in a second costume.
  method: "GET" | "POST" | "PATCH" | "DELETE";
  url: string;
  headers: Record<string, string>;
  /** `include` on every call: the session lives in a cookie the browser must attach. */
  credentials: "include";
  body?: string;
};

export type Membership = { org_id: string; name: string; role: string; role_label: string; is_external: boolean };

export type Me =
  | {
      authenticated: false;
      user: null;
      active_org: null;
      memberships: [];
      permissions: [];
      /** V17a. Always present, never optional — see the note on the branch below. */
      is_platform?: boolean;
    }
  | {
      authenticated: true;
      user: { id: string };
      // slug, country, timezone and currency are optional HERE and required in
      // the response, on purpose. They are read straight from the organisation
      // row through the tenant connection, and a client type that demanded them
      // would make every test fixture restate the database's shape. The screens
      // that need a timezone (V19 onward) narrow it at the point of use.
      /**
       * V17a. Verbunk staff, belonging to no organisation.
       *
       * OPTIONAL IN THE TYPE AND ALWAYS SENT BY THE API, for the reason the fields
       * below are: a client type that demanded it would make every existing test
       * fixture restate it. `isPlatformStaff` in `access.ts` narrows it at the one
       * point of use, so a fixture that omits it reads as "not staff" — which is the
       * safe direction, and matches `app.actor_is_external()` failing closed.
       */
      is_platform?: boolean;
      active_org:
        | {
            id: string;
            name: string;
            role: string | null;
            /** Tour T-12: the worded role, shown in the header. Optional: older API builds omit it. */
            role_label?: string | null;
            is_external: boolean;
            slug?: string;
            country?: string;
            timezone?: string;
            currency?: string;
            /** Tour T-20: whether people lists show names (0325). */
            show_people_names?: boolean;
          }
        | null;
      memberships: Membership[];
      permissions: string[];
      /**
       * V12. The bench rows this account IS, from app.actor_worker_ids().
       *
       * OPTIONAL HERE AND ALWAYS PRESENT IN THE RESPONSE, for the reason the
       * organisation's fields above are: a client type that demanded it would
       * make every fixture written before V12 restate it. The worker app narrows
       * it at the point of use, and an empty array is the ordinary case — most
       * people signing in are not on anybody's bench.
       */
      worker_ids?: string[];
      /** Sweep §4. The language this person chose; null follows the organisation's country. */
      locale?: string | null;
      /** Tour T-20: the person's own name, when they or an invitation gave one. */
      display_name?: string | null;
      session:{ expires_at: string | null; absolute_expiry: string | null };
    };

function json(base: string, path: string, payload: unknown, surface?: Surface): RequestSpec {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    [CSRF_HEADER]: CSRF_HEADER_VALUE,
  };
  if (surface) headers["X-Verbunk-Surface"] = surface;
  return {
    method: "POST",
    url: `${base}${path}`,
    headers,
    credentials: "include",
    // EVERY SECRET TRAVELS IN THE BODY, NEVER IN THE URL. A query string is
    // written to browser history, to every proxy access log, and to the Referer
    // header of the next request the page makes. A password or a reset token in
    // one is a credential published to infrastructure nobody audits.
    body: JSON.stringify(payload),
  };
}

export const requests = {
  /** Tour T-6 (0318). Who invited you, and to what. The token rides in a header: never a URL. */
  previewInvitation: (base: string, token: string): RequestSpec => ({
    method: "GET",
    url: `${base}/v1/auth/invitation/preview`,
    headers: { [CSRF_HEADER]: CSRF_HEADER_VALUE, "X-Verbunk-Invitation": token },
    credentials: "include",
  }),

  login: (base: string, email: string, password: string, surface: Surface = "console") =>
    json(base, "/v1/auth/login", { email, password }, surface),

  logout: (base: string) => json(base, "/v1/auth/logout", {}),
  logoutAll: (base: string) => json(base, "/v1/auth/logout-all", {}),

  /** A privilege change: the server issues a new session and revokes the old one (§4.1). */
  selectOrg: (base: string, orgId: string) => json(base, "/v1/auth/session/org", { org_id: orgId }),

  requestPasswordReset: (base: string, email: string, turnstileToken?: string) =>
    json(base, "/v1/auth/password-reset/request", { email, turnstile_token: turnstileToken }),
  confirmPasswordReset: (base: string, token: string, password: string) =>
    json(base, "/v1/auth/password-reset/confirm", { token, password }),

  requestMagicLink: (base: string, email: string) =>
    json(base, "/v1/auth/magic-link/request", { email }),
  consumeMagicLink: (base: string, token: string, surface: Surface = "worker") =>
    json(base, "/v1/auth/magic-link/consume", { token }, surface),

  /** Sweep S21a. The same constant answer as the magic link, whatever the address. */
  requestEmailVerification: (base: string, email: string) =>
    json(base, "/v1/auth/email-verify/request", { email }),
  confirmEmailVerification: (base: string, token: string) =>
    json(base, "/v1/auth/email-verify/confirm", { token }),

  /** Sweep S7. The emailed "stop these emails" link; the answer is a constant either way. */
  unsubscribe: (base: string, token: string) => json(base, "/v1/notifications/unsubscribe", { token }),

  /**
   * §6.6 — signup. Deferred from V3, built at V5.
   *
   * The response is DELIBERATELY UNINFORMATIVE and the screen must not
   * elaborate: it is byte-identical whether or not the address already has an
   * account, and no session is returned. See api/src/routes/auth.ts.
   */
  signup: (
    base: string,
    payload: {
      email: string;
      password: string;
      organisation_name: string;
      country: string;
      /** §5's challenge. Omitted only by a build with no site key. */
      turnstile_token?: string;
    },
  ) => json(base, "/v1/auth/signup", payload, "console"),

  /** §6.6 — accepting an invitation. Returns a session; the password is optional. */
  acceptInvitation: (base: string, token: string, password?: string) =>
    json(
      base,
      "/v1/auth/invitation/accept",
      password === undefined ? { token } : { token, password },
      "console",
    ),

  /** PUBLIC and read before anybody has proved anything: a key and a label,
   *  and an empty list on every deployment whose owner has not created the
   *  provider application. */
  oidcProviders: (base: string): RequestSpec => ({
    method: "GET",
    url: `${base}/v1/auth/oidc/providers`,
    headers: {},
    credentials: "include",
  }),

  /** Returns the URL to send the browser to, and sets the handshake cookie that
   *  binds the round trip to THIS browser. */
  oidcStart: (base: string, provider: string) =>
    json(base, "/v1/auth/oidc/start", { provider }, "console"),

  /** The code and state come back in the URL, and go to the server in the BODY
   *  — a code in a query string is written to history and to every proxy log. */
  oidcCallback: (base: string, payload: { provider: string; code: string; state: string }) =>
    json(base, "/v1/auth/oidc/callback", payload, "console"),

  /** S0-163. event.clinic's sign-in, exchanged for this console's session cookie. The
   *  Clerk session token travels as the bearer and never in the body. */
  clerkExchange: (base: string, clerkToken: string): RequestSpec => {
    const spec = json(base, "/v1/auth/clerk", { client: "browser" }, "console");
    return { ...spec, headers: { ...spec.headers, authorization: `Bearer ${clerkToken}` } };
  },

  /** Sweep §4. The person's own language; null goes back to following the organisation. */
  setLocale: (base: string, locale: string | null): RequestSpec => ({
    ...json(base, "/v1/me/locale", { locale }),
    method: "PATCH",
  }),

  /** Tour T-20. The person's own name; empty removes it. */
  setName: (base: string, name: string | null): RequestSpec => ({
    ...json(base, "/v1/me/name", { name }),
    method: "PATCH",
  }),

  me: (base: string): RequestSpec => ({
    method: "GET",
    url: `${base}/v1/me`,
    // NO ORG PARAMETER, ANYWHERE. §7: "a request with a valid session but a
    // tampered org parameter returns that user's real org — never the requested
    // one." The client has no way to ask for a different organisation because
    // the server has nowhere to read one from; switching is `selectOrg`, which
    // re-checks membership and issues a new session.
    headers: {},
    credentials: "include",
  }),
} as const;

/**
 * The one place a response is turned into a value.
 *
 * A 401 IS NOT AN EXCEPTION, IT IS AN ANSWER. `/v1/me` returning
 * `authenticated: false` is the normal first-paint case for a signed-out
 * visitor, and treating it as an error would make every cold load look like a
 * failure in the console.
 */
export async function send<T>(
  spec: RequestSpec,
  fetchImpl: typeof fetch = fetch,
): Promise<{ status: number; body: T }> {
  const init: RequestInit = {
    method: spec.method,
    headers: spec.headers,
    credentials: spec.credentials,
  };
  if (spec.body !== undefined) init.body = spec.body;
  const response = await fetchImpl(spec.url, init);
  const body = (await response.json().catch(() => ({}))) as T;
  return { status: response.status, body };
}

/**
 * Whether the signed-in person may do something.
 *
 * ADVISORY ONLY, and this is the sentence that matters. The permission list
 * comes from the server, and this function decides whether to SHOW a control —
 * never whether an action is allowed. Authorisation happens in the API
 * (api/src/rbac.ts, rule R7) and again in the database (row-level security). A
 * client-side check that could be bypassed by opening the developer console is
 * a user-interface affordance, not a security boundary, and must never be the
 * only one.
 */
export function may(me: Me, permission: string): boolean {
  return me.authenticated && me.permissions.includes(permission);
}
